/* eslint-disable @next/next/no-img-element */
import { X } from "lucide-react";
import type { SizeUpChip } from "./size-up-eligibility";
import { formatSubscriptionDateForDisplay } from "./formatters";

export interface SizeUpSubscriptionsBannerProps {
  onSizeUp: () => void;
  onDismiss: () => void;
  /** Chip for the subscription that can size up (name + next date). */
  chip?: SizeUpChip | null;
  /**
   * Title with the dollar amount replaced by `{amount}` so we can bold the
   * real saving, e.g. "Save {amount} by getting your product every 2 months".
   */
  titleTemplate: string;
  /** Formatted saving to inject into the title, e.g. "$8.48". */
  savingsAmount: string;
  /** e.g. "Switch to 64 servings every 2 months and enjoy extra savings". */
  body: string;
  /**
   * Primary CTA — same as cancel-chat bundle `acceptLabel`
   * (e.g. "Switch to 64 servings" / "Switch to 3-pack").
   */
  ctaLabel: string;
}

/**
 * Manage-tab banner offering to size up a Rose Nectar subscription
 * (same servings/month, billed less often). Visual twin of
 * {@link MergeSubscriptionsBanner}.
 */
export function SizeUpSubscriptionsBanner({
  onSizeUp,
  onDismiss,
  chip = null,
  titleTemplate,
  savingsAmount,
  body,
  ctaLabel,
}: SizeUpSubscriptionsBannerProps) {
  const amountIdx = titleTemplate.indexOf("{amount}");
  const titleBefore =
    amountIdx >= 0 ? titleTemplate.slice(0, amountIdx) : titleTemplate;
  const titleAfter =
    amountIdx >= 0 ? titleTemplate.slice(amountIdx + "{amount}".length) : "";

  return (
    <div className="tw:relative tw:flex tw:items-start tw:gap-3 tw:rounded-lg tw:border tw:border-[#523D33]/15 tw:bg-[#FDEDEE]/60 tw:p-3 tw:mb-3">
      <div className="tw:flex tw:flex-col tw:flex-1 tw:min-w-0 tw:pr-4">
        <span
          className="tw:text-sm tw:font-semibold tw:leading-snug"
          style={{ color: "var(--so-text, #523D33)" }}
        >
          {titleBefore}
          {amountIdx >= 0 ? (
            <span className="tw:font-bold">{savingsAmount}</span>
          ) : null}
          {titleAfter}
        </span>
        <span className="tw:text-xs tw:mt-1 tw:text-[#523D33]/70">{body}</span>
        {chip && (
          <div className="tw:flex tw:flex-wrap tw:gap-1.5 tw:mt-2">
            <span className="tw:inline-flex tw:items-center tw:gap-1.5 tw:max-w-full tw:rounded-full tw:bg-white tw:border tw:border-[#523D33]/15 tw:pl-1 tw:pr-2 tw:py-1">
              {chip.imageUrl ? (
                <img
                  src={chip.imageUrl}
                  alt={chip.title}
                  className="tw:size-5 tw:rounded-full tw:object-cover tw:shrink-0"
                  loading="lazy"
                />
              ) : (
                <span className="tw:size-5 tw:rounded-full tw:bg-[#523D33]/10 tw:text-[9px] tw:font-semibold tw:text-[#523D33] tw:flex tw:items-center tw:justify-center tw:shrink-0">
                  {chip.title.slice(0, 1).toUpperCase()}
                </span>
              )}
              <span className="tw:text-xs tw:font-semibold tw:text-[#523D33] tw:truncate">
                {chip.title}
              </span>
              {chip.nextBillingDate && (
                <span className="tw:text-[11px] tw:text-[#523D33]/60 tw:shrink-0">
                  {formatSubscriptionDateForDisplay(chip.nextBillingDate)}
                </span>
              )}
            </span>
          </div>
        )}
        <button
          type="button"
          onClick={onSizeUp}
          className="tw:self-start tw:mt-2 tw:rounded-md tw:bg-[#523D33] tw:text-[#FAC8CA] tw:text-xs tw:font-bold tw:uppercase tw:tracking-wide tw:px-3 tw:py-1.5 tw:border-none tw:cursor-pointer tw:transition-opacity hover:tw:opacity-90"
        >
          {ctaLabel}
        </button>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="tw:absolute tw:top-2 tw:right-2 tw:p-1 tw:rounded-md tw:border-none tw:bg-transparent tw:cursor-pointer tw:text-[#523D33]/60 hover:tw:text-[#523D33] hover:tw:bg-[#523D33]/5"
      >
        <X className="tw:size-4" />
      </button>
    </div>
  );
}
